import moment from "moment";

import { UNGROUPED_PSEUDO_GROUP_BY } from "~/common/groupBy";
import { getPropertyConfig } from "~/common/properties";
import { COMPLETED_STATUS_KINDS, SPRINT_DARTBOARD_KINDS_SET } from "~/components/visualization/constants";
import { DARTBOARD_KIND_TO_ICON_INFO_MAP } from "~/constants/dartboard";
import { UNCOLORED_PSEUDO_COLOR_BY } from "~/constants/pseudoDuids";
import { isDefined } from "~/shared/common";
import { DartboardKind, FilterConnector, IconKind, LayoutKind, PageKind, SprintMode, UserRole } from "~/shared/enums";
import type {
  Dartboard,
  DartboardUpdate,
  Layout,
  Property,
  Task,
  TaskAndUpdate,
  TaskUpdate,
  View,
} from "~/shared/types";
import type { PartialWithDuid } from "~/shared/typeUtils";
import { makeRandomColorHex } from "~/utils/color";
import { deepCopy, getNextTitleInSequence, makeDuid, randomSample } from "~/utils/common";
import { makeDartboardComparator } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";
import { getNextRecursNextAt } from "~/utils/time";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToDartboards: Map<string, Dartboard>;
  _sortedDartboardDuids: string[];
};

export const state: State = {
  _duidsToDartboards: new Map(),
  _sortedDartboardDuids: [],
};

export type Getters = {
  getDartboardList: (options?: { includeHidden?: boolean; excludeFinished?: boolean }) => Dartboard[];
  getDartboardByDuid: (duid: string) => Dartboard | undefined;
  getDartboardsByDuidsOrdered: (duids: string[]) => Dartboard[];
  getDartboardsBySpaceDuidOrdered: (
    spaceDuid: string,
    options?: { includeHidden?: boolean; excludeFinished?: boolean }
  ) => Dartboard[];
  getDartboardsByKindOrdered: (kind: DartboardKind, options?: { includeHidden?: boolean }) => Dartboard[];
  getWorkspaceDartboardByKind: (kind: DartboardKind) => Dartboard | undefined;
  defaultDartboard: Dartboard | undefined;
};

export type Actions = {
  createDartboard: (
    spaceDuid: string,
    order: string,
    partialDartboard?: Partial<Dartboard>,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<Dartboard>;
  updateDartboard: (
    update: DartboardUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Dartboard | undefined>;
  replicateDartboard: (
    dartboard: Dartboard,
    order: string,
    partialDartboard?: Partial<Dartboard>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<Dartboard | undefined>;
  importViewAsDartboard: (
    view: View,
    layout: Layout,
    properties: Property[],
    spaceDuid: string,
    order: string
  ) => Promise<Dartboard | undefined>;
  deleteDartboard: (dartboard: Dartboard, options?: { noBackend?: boolean; awaitBackend?: boolean }) => Promise<void>;
  createSprintDartboardsNoBackend: (spaceDuid: string) => Promise<Dartboard[]>;
  deleteSprintDartboardsNoBackend: (spaceDuid: string) => Promise<string[]>;
  rollover: (spaceDuid: string) => Promise<void>;
  reverseRollover: (spaceDuid: string) => Promise<void>;
  $cleanupDartboardsPropertyReferencesNoBackend: (propertyDuids: string[]) => void;
  $setDartboards: (dartboards: Dartboard[]) => void;
  $createDartboard: (dartboard: Dartboard) => void;
  $updateDartboard: (dartboard: PartialWithDuid<Dartboard>) => void;
  $saveDartboard: (dartboard: PartialWithDuid<Dartboard>) => void;
  $deleteDartboard: (dartboard: Dartboard) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getDartboardList() {
    return (options = {}) => {
      const { includeHidden = false, excludeFinished = false } = options;
      const tenantStore = this.$useTenantStore();
      return this._sortedDartboardDuids
        .map((e) => this._duidsToDartboards.get(e))
        .filter(isDefined)
        .filter(
          (e) =>
            (!excludeFinished || e.kind !== DartboardKind.FINISHED) &&
            (includeHidden || !(e.kind === DartboardKind.BACKLOG && !tenantStore.backlogEnabled))
        );
    };
  },
  getDartboardByDuid() {
    return (duid) => this._duidsToDartboards.get(duid);
  },
  getDartboardsByDuidsOrdered() {
    return (duids) => this.getDartboardList({ includeHidden: true }).filter((e) => duids.includes(e.duid));
  },
  getDartboardsBySpaceDuidOrdered() {
    return (spaceDuid, options) => this.getDartboardList(options).filter((e) => e.spaceDuid === spaceDuid);
  },
  getDartboardsByKindOrdered() {
    return (kind, options) => this.getDartboardList(options).filter((db) => db.kind === kind);
  },
  getWorkspaceDartboardByKind() {
    return (kind) => {
      const { workspaceSpace } = this;
      if (workspaceSpace === undefined) {
        return undefined;
      }
      return this.getDartboardsBySpaceDuidOrdered(workspaceSpace.duid, { includeHidden: true }).filter(
        (e) => e.kind === kind
      )[0];
    };
  },
  defaultDartboard() {
    if (!this.$useUserStore().isRoleGte(UserRole.MEMBER)) {
      return this.getDartboardList()[0];
    }
    return this.workspaceSpace?.sprintMode === SprintMode.ANBA
      ? this.getWorkspaceDartboardByKind(DartboardKind.ACTIVE)
      : this.getWorkspaceDartboardByKind(DartboardKind.CUSTOM);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createDartboard(spaceDuid, order, partialDartboard, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;
    const appStore = this.$useAppStore();

    const newLayout = this.makeLayoutNoBackend();

    const userDuid = this.$useUserStore().duid;
    const newDartboard: Dartboard = {
      pageKind: PageKind.DARTBOARD,
      spaceDuid,
      duid: makeDuid(),
      kind: DartboardKind.CUSTOM,
      order,
      topTaskOrder: "",
      title: "",
      description: "",
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      userDuidsToLayoutDuids: [{ userDuid, layoutDuid: newLayout.duid }],
      index: null,
      startedAt: null,
      finishedAt: null,
      plannedFinishAt: null,
      alwaysShownPropertyDuids: [],
      alwaysHiddenPropertyDuids: [],
      propertyOrderDuids: [],
      propertyWidthMap: {},
      ...partialDartboard,
    };

    this.$createDartboard(newDartboard);

    if (!noBackend) {
      const userDartboardLayout = {
        userDuid,
        dartboardDuid: newDartboard.duid,
        layoutDuid: newLayout.duid,
      };

      const backendAction = this.$backend.dartboard.create(newDartboard, newLayout, userDartboardLayout);
      if (awaitBackend) {
        await backendAction;
      }
    }

    return newDartboard;
  },
  async updateDartboard(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const dartboard = this.getDartboardByDuid(update.duid);
    if (!dartboard) {
      return undefined;
    }

    Object.assign(dartboard, update);
    updateInSorted(this._duidsToDartboards, this._sortedDartboardDuids, dartboard.duid, (a, b) =>
      makeDartboardComparator(this.getSpaceByDuid)(a, b)
    );

    if (!noBackend) {
      const backendAction = this.$backend.dartboard.update(update);
      if (awaitBackend) {
        await backendAction;
      }
    }

    return dartboard;
  },
  async replicateDartboard(dartboard, order, partialDartboard, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;
    const oldLayout = this.getLayoutByDartboardDuid(dartboard.duid);
    let newLayout;
    if (oldLayout) {
      newLayout = {
        ...deepCopy(oldLayout),
        duid: makeDuid(),
      };
      this.$createOrUpdateLayout(newLayout);
    } else {
      newLayout = this.makeLayoutNoBackend();
    }

    const userDuid = this.$useUserStore().duid;
    const newDartboard: Dartboard = {
      ...deepCopy(dartboard),
      duid: makeDuid(),
      kind: DartboardKind.CUSTOM,
      order,
      userDuidsToLayoutDuids: [{ userDuid, layoutDuid: newLayout.duid }],
      ...partialDartboard,
    };
    if (!maintainTitle && partialDartboard?.title === undefined) {
      newDartboard.title = getNextTitleInSequence(
        dartboard.title,
        this.getDartboardsBySpaceDuidOrdered(newDartboard.spaceDuid).map((e) => e.title)
      );
    }

    this.$createDartboard(newDartboard);

    const userDartboardLayout = {
      userDuid,
      dartboardDuid: newDartboard.duid,
      layoutDuid: newLayout.duid,
    };

    // TODO transactionify these two calls
    const backendAction = (async () => {
      await this.$backend.dartboard.create(newDartboard, newLayout, userDartboardLayout);
      await this.$backendOld.dartboards.replicate(dartboard.duid, { targetDuid: newDartboard.duid });
    })();

    if (awaitBackend) {
      await backendAction;
    }

    return newDartboard;
  },
  async importViewAsDartboard(view, layout, properties, spaceDuid, order) {
    const customProperties = properties.filter((e) => !getPropertyConfig(e.kind).isDefault);
    const defaultProperties = properties.filter((e) => getPropertyConfig(e.kind).isDefault);
    const propertyCreationPromises: Promise<{ oldDuid: string; newDuid: string | undefined }>[] = customProperties.map(
      async (templateProperty) => {
        const existingProperty = this.getPropertyByDuid(templateProperty.duid);
        if (existingProperty) {
          return { oldDuid: templateProperty.duid, newDuid: existingProperty.duid };
        }
        const similarProperty = this.propertyList.find(
          (e) => e.title === templateProperty.title && e.kind === templateProperty.kind
        );
        if (similarProperty) {
          return { oldDuid: templateProperty.duid, newDuid: similarProperty.duid };
        }

        const createdProperty = await this.createProperty(
          templateProperty.kind,
          templateProperty.order,
          {
            title: templateProperty.title,
            description: templateProperty.description,
            adtl: "adtl" in templateProperty ? templateProperty.adtl : undefined,
            hidden: true,
          },
          { awaitBackend: true }
        );
        return { oldDuid: templateProperty.duid, newDuid: createdProperty.duid };
      }
    );

    const propertyMappings = (await Promise.all(propertyCreationPromises))
      .concat(
        defaultProperties.map((e) => ({
          oldDuid: e.duid,
          newDuid: this.propertyList.find((f) => f.kind === e.kind)?.duid,
        }))
      )
      .filter((e): e is { oldDuid: string; newDuid: string } => isDefined(e.newDuid));
    const propertyDuidMap = Object.fromEntries(propertyMappings.map((e) => [e.oldDuid, e.newDuid]));
    const remappedPropertyOrderDuids = view.propertyOrderDuids
      .map((oldDuid) => propertyDuidMap[oldDuid])
      .filter((duid) => this.getPropertyByDuid(duid));
    const remappedPropertyWidthMap = Object.fromEntries(
      Object.entries(view.propertyWidthMap ?? {})
        .map(([oldDuid, width]) => [propertyDuidMap[oldDuid], width] satisfies [string, number])
        .filter(([duid]) => this.getPropertyByDuid(duid))
    );

    const newLayout: Layout = {
      ...layout,
      duid: makeDuid(),
      filterGroup: {
        filters: [],
        connector: FilterConnector.AND,
      },
      sorts: layout.sorts
        .map((sort) => ({
          ...sort,
          propertyDuid: propertyDuidMap[sort.propertyDuid],
        }))
        .filter((e) => isDefined(e.propertyDuid)),
    };
    Object.entries(layout.kindConfigMap).forEach(([kind, config]) => {
      switch (kind) {
        case LayoutKind.LIST:
        case LayoutKind.BOARD: {
          newLayout.kindConfigMap[kind] = {
            ...config,
            groupBy:
              "groupBy" in config && propertyDuidMap[config.groupBy]
                ? propertyDuidMap[config.groupBy]
                : UNGROUPED_PSEUDO_GROUP_BY,
          };
          break;
        }
        case LayoutKind.CALENDAR:
        case LayoutKind.ROADMAP: {
          newLayout.kindConfigMap[kind] = {
            ...config,
            colorBy:
              "colorBy" in config && propertyDuidMap[config.colorBy]
                ? propertyDuidMap[config.colorBy]
                : UNCOLORED_PSEUDO_COLOR_BY,
          };
          break;
        }
        default: {
          throw new Error(`Unsupported layout kind: ${kind}`);
        }
      }
    });
    this.$createOrUpdateLayout(newLayout);

    const userDuid = this.$useUserStore().duid;
    const newDartboard: Dartboard = {
      pageKind: PageKind.DARTBOARD,
      spaceDuid,
      duid: makeDuid(),
      kind: DartboardKind.CUSTOM,
      order,
      topTaskOrder: "",
      title: view.title,
      description: view.description,
      iconKind: view.iconKind,
      iconNameOrEmoji: view.iconNameOrEmoji,
      colorHex: view.colorHex,
      userDuidsToLayoutDuids: [{ userDuid, layoutDuid: newLayout.duid }],
      index: null,
      startedAt: null,
      finishedAt: null,
      plannedFinishAt: null,
      alwaysShownPropertyDuids: propertyMappings.map((e) => e.newDuid),
      alwaysHiddenPropertyDuids: [],
      propertyOrderDuids: remappedPropertyOrderDuids,
      propertyWidthMap: remappedPropertyWidthMap,
    };
    this.$createDartboard(newDartboard);

    // TODO transactionify these two calls
    const userDartboardLayout = {
      userDuid,
      dartboardDuid: newDartboard.duid,
      layoutDuid: newLayout.duid,
    };
    await this.$backend.dartboard.create(newDartboard, newLayout, userDartboardLayout);

    this.$backendOld.views.importTemplateView(view.duid, newDartboard.duid, propertyDuidMap);

    return newDartboard;
  },
  async deleteDartboard(dartboard, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;

    let destDartboardDuid = this.defaultDartboard?.duid;
    if (destDartboardDuid === dartboard.duid) {
      destDartboardDuid = this.getDartboardList().find((e) => e.duid !== dartboard.duid)?.duid;
    }

    // TODO transactionify
    const updates = this.getLoadedTasksForPageDuidOrdered(dartboard.duid, undefined, {
      includeTrashed: true,
      includeDraft: true,
    }).map((e) => {
      const res: TaskUpdate = {
        duid: e.duid,
      };
      if (destDartboardDuid) {
        res.dartboardDuid = destDartboardDuid;
      }
      if (!e.inTrash && !e.drafterDuid) {
        res.inTrash = true;
      }
      return res;
    });

    await this.updateTasks(updates, { noUndo: true, noBackend: true });

    this.$deleteDartboard(dartboard);

    this.$cleanupLayoutsFiltersNoBackend([dartboard.duid], [this.defaultDartboardProperty.duid]);

    if (!noBackend) {
      const backendAction = this.$backend.dartboard.delete(dartboard.duid);
      if (awaitBackend) {
        await backendAction;
      }
    }
  },
  async createSprintDartboardsNoBackend(spaceDuid) {
    const topOrder = this.getDartboardsBySpaceDuidOrdered(spaceDuid)[0]?.order;
    const [activeOrder, nextOrder, backlogOrder] = getOrdersBetween(undefined, topOrder, 3);

    const activeConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.ACTIVE];
    const partialActive = {
      kind: DartboardKind.ACTIVE,
      title: "Sprint 1",
      iconKind: IconKind.ICON,
      iconNameOrEmoji: activeConfig.iconNameOrEmoji,
      colorHex: activeConfig.colorHex,
      index: 1,
      startedAt: new Date().toISOString(),
    };

    const nextConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.NEXT];
    const partialNext = {
      kind: DartboardKind.NEXT,
      title: "Sprint 2",
      iconKind: IconKind.ICON,
      iconNameOrEmoji: nextConfig.iconNameOrEmoji,
      colorHex: nextConfig.colorHex,
      index: 2,
    };

    const backlogConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.BACKLOG];
    const partialBacklog = {
      kind: DartboardKind.BACKLOG,
      title: "Backlog Tasks",
      iconKind: IconKind.ICON,
      iconNameOrEmoji: backlogConfig.iconNameOrEmoji,
      colorHex: backlogConfig.colorHex,
    };

    const active = await this.createDartboard(spaceDuid, activeOrder, partialActive, { noBackend: true });
    const next = await this.createDartboard(spaceDuid, nextOrder, partialNext, { noBackend: true });
    const backlog = await this.createDartboard(spaceDuid, backlogOrder, partialBacklog, { noBackend: true });

    return [active, next, backlog];
  },
  async deleteSprintDartboardsNoBackend(spaceDuid) {
    const allDartboards = this.getDartboardsBySpaceDuidOrdered(spaceDuid, { includeHidden: true });
    const sprintDartboards = allDartboards.filter((e) => SPRINT_DARTBOARD_KINDS_SET.has(e.kind));
    const willHaveNoDartboardsLeft = allDartboards.every((e) => SPRINT_DARTBOARD_KINDS_SET.has(e.kind));
    sprintDartboards.forEach((e) => this.deleteDartboard(e, { noBackend: true }));

    // If no dartboards are left in workspace space, create a default dartboard
    if (this.workspaceSpace?.duid === spaceDuid && willHaveNoDartboardsLeft) {
      await this.createDartboard(spaceDuid, String.fromCharCode(129), {
        title: "Tasks",
        iconKind: IconKind.ICON,
        iconNameOrEmoji: "TaskIcon",
        colorHex: "#b49bf8",
      });
    }

    return sprintDartboards.map((e) => e.duid);
  },
  async rollover(spaceDuid) {
    const space = this.getSpaceByDuid(spaceDuid);
    const spaceDartboards = this.getDartboardsBySpaceDuidOrdered(spaceDuid);
    const oldActiveDartboard = spaceDartboards.find((db) => db.kind === DartboardKind.ACTIVE);
    const oldNextDartboard = spaceDartboards.find((db) => db.kind === DartboardKind.NEXT);
    if (!space || !oldActiveDartboard || !oldNextDartboard) {
      return;
    }

    const now = new Date().toISOString();
    const newNextDartboardDuid = makeDuid();

    // move all tasks that aren't completed (i.e. finished or cancelled status kind) from active to next
    const taskMovesToNext: TaskUpdate[] = [];
    const taskReplicates: TaskAndUpdate[] = [];
    this.getAllLoadedTasksForUiOnly({ includeTrashed: false, includeDraft: false })
      .filter((e) => e.dartboardDuid === oldActiveDartboard.duid)
      .forEach((task) => {
        const statusKind = this.getStatusByDuid(task.statusDuid)?.kind;
        if (!statusKind) {
          return;
        }

        if (!COMPLETED_STATUS_KINDS.has(statusKind)) {
          if (space.sprintReplicateOnRollover) {
            taskReplicates.push({
              task,
              order: task.order,
              partialTask: { dartboardDuid: oldNextDartboard.duid },
            });
          } else {
            // eslint-disable-next-line no-param-reassign
            task.dartboardDuid = oldNextDartboard.duid;

            taskMovesToNext.push({
              duid: task.duid,
              dartboardDuid: oldNextDartboard.duid,
            });
          }
        }
      });

    // replicate layouts for the new next
    const {
      layoutCreates: newNextLayoutCreates,
      userDartboardLayouts: newNextUserDartboardLayouts,
      userDartboardLayoutCreates: newNextUserDartboardLayoutCreates,
    } = await this.$replicateLayoutsForDartboardNoBackend(oldNextDartboard.duid, newNextDartboardDuid);

    // replicate layouts for the new active
    const {
      layoutUpdates: newActiveLayoutUpdates,
      layoutCreates: newActiveLayoutCreates,
      userDartboardLayouts: newActiveUserDartboardLayouts,
      userDartboardLayoutCreates: newActiveLayoutsUserDartboardLayoutCreates,
    } = await this.$replicateLayoutsForDartboardNoBackend(oldActiveDartboard.duid, oldNextDartboard.duid);

    // cleanup existing layouts for old active
    const oldActiveLayoutUpdates = (
      await Promise.all(
        oldActiveDartboard.userDuidsToLayoutDuids.map((e) =>
          this.updateLayout(this.makeLayoutNoBackend({ duid: e.layoutDuid }), { noBackend: true })
        )
      )
    ).filter(isDefined);

    // update old active
    const finishedConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.FINISHED];
    const oldActiveUpdate = {
      duid: oldActiveDartboard.duid,
      kind: DartboardKind.FINISHED,
      iconNameOrEmoji: finishedConfig.iconNameOrEmoji,
      colorHex: finishedConfig.colorHex,
      finishedAt: now,
    };
    Object.assign(oldActiveDartboard, oldActiveUpdate);

    let oldNextNewPlannedFinishedAt = oldNextDartboard.plannedFinishAt;
    let newNextPlannedFinishAt = null;

    if (space.rolloverRecurrence) {
      oldNextNewPlannedFinishedAt = space.rolloverRecursNextAt;
      newNextPlannedFinishAt = getNextRecursNextAt(
        space.rolloverRecurrence,
        moment(space.rolloverRecursNextAt)
      ).toISOString();
    }

    const oldNextProperties = {
      alwaysShownPropertyDuids: [...oldNextDartboard.alwaysShownPropertyDuids],
      alwaysHiddenPropertyDuids: [...oldNextDartboard.alwaysHiddenPropertyDuids],
      propertyOrderDuids: [...oldNextDartboard.propertyOrderDuids],
      propertyWidthMap: { ...oldNextDartboard.propertyWidthMap },
    };

    // update old next
    const activeConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.ACTIVE];
    const oldNextUpdate = {
      duid: oldNextDartboard.duid,
      kind: DartboardKind.ACTIVE,
      iconNameOrEmoji: activeConfig.iconNameOrEmoji,
      colorHex: activeConfig.colorHex,
      startedAt: now,
      plannedFinishAt: oldNextNewPlannedFinishedAt,
      alwaysShownPropertyDuids: [...oldActiveDartboard.alwaysShownPropertyDuids],
      alwaysHiddenPropertyDuids: [...oldActiveDartboard.alwaysHiddenPropertyDuids],
      propertyOrderDuids: [...oldActiveDartboard.propertyOrderDuids],
      propertyWidthMap: { ...oldActiveDartboard.propertyWidthMap },
    };
    Object.assign(oldNextDartboard, oldNextUpdate);
    oldNextDartboard.userDuidsToLayoutDuids.push(...newActiveUserDartboardLayouts);

    // make a new dartboard with next kind and index and name one higher than the last next
    const nextConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.NEXT];
    const newNextIndex = (oldNextDartboard.index ?? 0) + 1;
    const newNextDartboard = {
      ...oldNextDartboard,
      duid: newNextDartboardDuid,
      kind: DartboardKind.NEXT,
      order: getOrdersBetween(undefined, oldNextDartboard.order)[0],
      title: `Sprint ${newNextIndex}`,
      iconNameOrEmoji: nextConfig.iconNameOrEmoji,
      colorHex: nextConfig.colorHex,
      userDuidsToLayoutDuids: newNextUserDartboardLayouts,
      index: newNextIndex,
      startedAt: null,
      plannedFinishAt: newNextPlannedFinishAt,
      alwaysShownPropertyDuids: [...oldNextProperties.alwaysShownPropertyDuids],
      alwaysHiddenPropertyDuids: [...oldNextProperties.alwaysHiddenPropertyDuids],
      propertyOrderDuids: [...oldNextProperties.propertyOrderDuids],
      propertyWidthMap: { ...oldNextProperties.propertyWidthMap },
    };

    this.$createDartboard(newNextDartboard);

    const dartboardDuidRemap = new Map([
      [oldNextDartboard.duid, newNextDartboard.duid],
      [oldActiveDartboard.duid, oldNextDartboard.duid],
    ]);
    // Find any layouts that use the old next & active duids and update them to use the new duids
    const filterLayoutUpdates = this.$updateLayoutsRemapDartboardFiltersNoBackend(dartboardDuidRemap);
    const automationUpdates = this.$updateAutomationsRemapDartboardNoBackend(dartboardDuidRemap);

    await this.$backend.dartboard.rollover(
      taskMovesToNext,
      [oldActiveUpdate, oldNextUpdate],
      newNextDartboard,
      [...oldActiveLayoutUpdates, ...newActiveLayoutUpdates, ...filterLayoutUpdates],
      [...newActiveLayoutCreates, ...newNextLayoutCreates],
      [...newActiveLayoutsUserDartboardLayoutCreates, ...newNextUserDartboardLayoutCreates],
      automationUpdates
    );
    await this.replicateTasks(taskReplicates, {
      noUndo: true,
      includeDuplicate: true,
      maintainTitle: true,
      maintainStatus: true,
    });
  },
  async reverseRollover(spaceDuid) {
    const dartboards = this.getDartboardsBySpaceDuidOrdered(spaceDuid);
    const oldActiveDartboard = dartboards.find((e) => e.kind === DartboardKind.ACTIVE);
    const oldNextDartboard = dartboards.find((e) => e.kind === DartboardKind.NEXT);
    const oldFinishedDartboard = dartboards.find((e) => e.kind === DartboardKind.FINISHED);
    if (!oldActiveDartboard || !oldNextDartboard || !oldFinishedDartboard) {
      return;
    }

    const tasksMovesToFinished: TaskUpdate[] = [];
    const tasksMovesToActive: TaskUpdate[] = [];

    // replicate layouts for the new active
    const {
      layoutUpdates: newActiveLayoutUpdates,
      layoutCreates: newActiveLayoutCreates,
      userDartboardLayouts: newActiveUserDartboardLayouts,
      userDartboardLayoutCreates: newActiveLayoutsUserDartboardLayoutCreates,
    } = await this.$replicateLayoutsForDartboardNoBackend(oldActiveDartboard.duid, oldFinishedDartboard.duid);

    // replicate layouts for the new next
    const {
      layoutUpdates: newNextLayoutUpdates,
      layoutCreates: newNextLayoutCreates,
      userDartboardLayoutCreates: newNextUserDartboardLayoutCreates,
    } = await this.$replicateLayoutsForDartboardNoBackend(oldNextDartboard.duid, oldActiveDartboard.duid);

    // update old finished
    const activeConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.ACTIVE];
    const newActiveUpdate = {
      duid: oldFinishedDartboard.duid,
      kind: DartboardKind.ACTIVE,
      iconNameOrEmoji: activeConfig.iconNameOrEmoji,
      colorHex: activeConfig.colorHex,
      finishedAt: null,
    };
    Object.assign(oldFinishedDartboard, newActiveUpdate);
    oldFinishedDartboard.userDuidsToLayoutDuids.push(...newActiveUserDartboardLayouts);

    // move all tasks from next to active
    this.getLoadedTasksForPageDuidOrdered(oldNextDartboard.duid).forEach((task) => {
      // eslint-disable-next-line no-param-reassign
      task.dartboardDuid = oldActiveDartboard.duid;
      tasksMovesToActive.push({
        duid: task.duid,
        dartboardDuid: oldActiveDartboard.duid,
      });
    });

    // update old active
    const nextConfig = DARTBOARD_KIND_TO_ICON_INFO_MAP[DartboardKind.NEXT];
    const newNextUpdate = {
      duid: oldActiveDartboard.duid,
      kind: DartboardKind.NEXT,
      iconNameOrEmoji: nextConfig.iconNameOrEmoji,
      colorHex: nextConfig.colorHex,
      startedAt: null,
    };
    Object.assign(oldActiveDartboard, newNextUpdate);

    // delete old next
    this.$deleteDartboard(oldNextDartboard);

    await this.$backend.dartboard.reverseRollover(
      [...tasksMovesToFinished, ...tasksMovesToActive],
      [newActiveUpdate, newNextUpdate],
      [...newActiveLayoutUpdates, ...newNextLayoutUpdates],
      [...newActiveLayoutCreates, ...newNextLayoutCreates],
      [...newActiveLayoutsUserDartboardLayoutCreates, ...newNextUserDartboardLayoutCreates],
      oldNextDartboard.duid
    );
  },
  $cleanupDartboardsPropertyReferencesNoBackend(propertyDuids) {
    const propertyDuidSet = new Set(propertyDuids);

    const dartboardUpdates = this.getDartboardList().map((dartboard) => ({
      duid: dartboard.duid,
      alwaysShownPropertyDuids: dartboard.alwaysShownPropertyDuids.filter((duid) => !propertyDuidSet.has(duid)),
      alwaysHiddenPropertyDuids: dartboard.alwaysHiddenPropertyDuids.filter((duid) => !propertyDuidSet.has(duid)),
      propertyOrderDuids: dartboard.propertyOrderDuids.filter((duid) => !propertyDuidSet.has(duid)),
      propertyWidthMap: Object.fromEntries(
        Object.entries(dartboard.propertyWidthMap).filter(([duid]) => !propertyDuidSet.has(duid))
      ),
    }));

    dartboardUpdates.forEach((update) => this.updateDartboard(update, { noBackend: true }));
  },
  $setDartboards(dartboards) {
    setObjectMapAndSorted(
      this._duidsToDartboards,
      this._sortedDartboardDuids,
      dartboards.map((e) => ({ ...e, pageKind: PageKind.DARTBOARD }) satisfies Dartboard),
      makeDartboardComparator(this.getSpaceByDuid)
    );
  },
  async $createDartboard(dartboard) {
    const currentDartboard = this.getDartboardByDuid(dartboard.duid);

    // TODO WS some of this logic will change when we are filtering out spurious updates on BE of WS
    const space = this.getSpaceByDuid(dartboard.spaceDuid);
    if (!space) {
      return;
    }
    if (!(space.accessibleByTeam || space.accessibleByUserDuids.includes(this.$useUserStore().duid))) {
      if (currentDartboard) {
        this.$deleteDartboard(dartboard);
      }
      return;
    }

    /* Create */
    if (!currentDartboard) {
      // eslint-disable-next-line no-param-reassign
      dartboard.pageKind = PageKind.DARTBOARD;

      this._duidsToDartboards.set(dartboard.duid, dartboard);
      updateInSorted(this._duidsToDartboards, this._sortedDartboardDuids, dartboard.duid, (a, b) =>
        makeDartboardComparator(this.getSpaceByDuid)(a, b)
      );

      // TODO shouldn't rely on client to get the tasks separately, this should probably come over WS
      const { data } = await this.$backendOld.tasks.getByDartboard(dartboard.duid);
      data.items.forEach((task: Task) => {
        this.$createOrUpdateTask(task);
      });
      return;
    }

    this.$saveDartboard(dartboard);
  },
  $updateDartboard(dartboard) {
    this.$saveDartboard(dartboard);
  },
  $saveDartboard(dartboard) {
    const currentDartboard = this.getDartboardByDuid(dartboard.duid);
    if (!currentDartboard) {
      return;
    }

    Object.assign(currentDartboard, dartboard);
    currentDartboard.pageKind = PageKind.DARTBOARD;

    updateInSorted(this._duidsToDartboards, this._sortedDartboardDuids, currentDartboard.duid, (a, b) =>
      makeDartboardComparator(this.getSpaceByDuid)(a, b)
    );
  },
  $deleteDartboard(dartboard) {
    const deletingCurrentPage = dartboard.duid === this.$useAppStore().currentPage?.duid;

    this._duidsToDartboards.delete(dartboard.duid);
    removeFromSorted(this._sortedDartboardDuids, dartboard.duid);

    // TODO shouldn't rely on client to delete these, this should probably come over WS; also this is bad because it can be bad for the normal delete flow
    this.getLoadedTasksForPageDuidOrdered(dartboard.duid, undefined, {
      includeTrashed: true,
      includeDraft: true,
    }).forEach(this.$deleteTask);

    if (deletingCurrentPage) {
      this.$routerUtils.goHomeNextTick();
    }
  },
};

export { actions, getters };
